import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Building2,
  Briefcase,
  Phone,
  Globe,
  RefreshCw,
  Save,
  X,
  Settings,
  CheckCircle2,
  Clock,
  Shield,
  Bell,
  BellOff,
  Moon,
  Sun,
  Monitor,
  AlertCircle,
  MessageSquare,
  Star,
  Send,
  Bug,
  Lightbulb,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  FolderPlus,
  Upload,
  Search,
  Circle
} from 'lucide-react';
import {
  apiService,
  UserProfile,
  UserSettings,
  UpdateProfileRequest,
  UpdateSettingsRequest,
  FeedbackEntry,
  SubmitFeedbackRequest,
  ProcessingModelOption
} from '../services/api';
import { commitOwnedSearch, HistoryMode } from '../navigation/historyState';
import { parseEnumParam } from '../navigation/urlCodec';
import { openStorageSettings } from '../privacy/storageConsent';
import { useTabActivity } from '../contexts/TabActivityContext';
import './ProfileTab.css';

type ActiveSection = 'profile' | 'settings' | 'feedback';
type ThemeMode = 'light' | 'dark' | 'system';
// Optional product milestones, independent of historical signup completion.
const GETTING_STARTED_STEPS = [
  {
    key: 'create_index',
    label: 'Create an Index',
    description: 'Create your first index to organize your media',
    icon: FolderPlus
  },
  {
    key: 'upload_video',
    label: 'Upload Media',
    description: 'Upload your first video or audio file',
    icon: Upload
  },
  {
    key: 'run_search',
    label: 'Run a Search',
    description: 'Search through your processed content',
    icon: Search
  }
];

const ProfileTab: React.FC = () => {
  // Data state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [processingModelOptions, setProcessingModelOptions] = useState<ProcessingModelOption[]>([]);
  const [isLoadingModelOptions, setIsLoadingModelOptions] = useState(false);
  const [modelOptionsError, setModelOptionsError] = useState<string | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<ActiveSection>('profile');

  // Edit mode state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState<UpdateProfileRequest>({});

  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Feedback state
  const [feedbackList, setFeedbackList] = useState<FeedbackEntry[]>([]);
  const [feedbackCursor, setFeedbackCursor] = useState<string | null>(null);
  const [feedbackHasMore, setFeedbackHasMore] = useState(false);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState<string | null>(null);
  const [feedbackForm, setFeedbackForm] = useState<SubmitFeedbackRequest>({
    rating: 0,
    body: '',
    category: null
  });
  const [urlHydrated, setUrlHydrated] = useState(false);
  const historyModeRef = React.useRef<HistoryMode>('replace');
  const isApplyingUrlRef = React.useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isActive } = useTabActivity();

  useEffect(() => {
    isApplyingUrlRef.current = true;
    const params = new URLSearchParams(location.search);
    setActiveSection(parseEnumParam(params.get('section'), ['profile', 'settings', 'feedback'] as const, 'profile'));

    const timer = window.setTimeout(() => {
      isApplyingUrlRef.current = false;
      setUrlHydrated(true);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.search]);

  useEffect(() => {
    if (!isActive || !urlHydrated || isApplyingUrlRef.current) return;

    const mode = historyModeRef.current;
    historyModeRef.current = 'replace';
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: ['section'],
      updates: { section: activeSection },
      mode,
    });
  }, [isActive, activeSection, location, navigate, urlHydrated]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [profileData, settingsData] = await Promise.all([
        apiService.getUserProfile(),
        apiService.getUserSettings()
      ]);
      setProfile(profileData);
      setSettings(settingsData);

      setIsLoadingModelOptions(true);
      setModelOptionsError(null);
      try {
        const pricingConfig = await apiService.getBillingPricingConfig();
        const activeOptions = (pricingConfig.processing_model_options || []).filter((option) => option.is_active);
        if (activeOptions.length === 0) {
          throw new Error('No active processing models are configured');
        }
        setProcessingModelOptions(activeOptions);
      } catch (modelErr) {
        console.error('Failed to load processing model options:', modelErr);
        setProcessingModelOptions([]);
        setModelOptionsError('Processing model options are unavailable.');
      } finally {
        setIsLoadingModelOptions(false);
      }
    } catch (err: any) {
      console.error('Failed to load profile data:', err);
      setError(err.response?.data?.message || 'Failed to load profile data');
      setProcessingModelOptions([]);
      setModelOptionsError('Processing model options are unavailable.');
      setIsLoadingModelOptions(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadFeedback = useCallback(async (reset: boolean = false) => {
    setIsLoadingFeedback(true);
    try {
      const cursor = reset ? undefined : (feedbackCursor || undefined);
      const response = await apiService.listFeedback(20, cursor);
      if (reset) {
        setFeedbackList(response.feedback);
      } else {
        setFeedbackList(prev => [...prev, ...response.feedback]);
      }
      setFeedbackCursor(response.next_cursor);
      setFeedbackHasMore(response.has_more);
    } catch (err: any) {
      console.error('Failed to load feedback:', err);
      setError(err.response?.data?.message || 'Failed to load feedback');
    } finally {
      setIsLoadingFeedback(false);
    }
  }, [feedbackCursor]);

  // Load account data once through a stable callback.
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load feedback on first entry; the length guard prevents cursor changes
  // from re-fetching an already hydrated list.
  useEffect(() => {
    if (activeSection === 'feedback' && feedbackList.length === 0) {
      loadFeedback(true);
    }
  }, [activeSection, feedbackList.length, loadFeedback]);

  const handleSubmitFeedback = async () => {
    if (feedbackForm.rating === 0 || !feedbackForm.body.trim()) {
      setError('Please provide a rating and feedback message');
      return;
    }

    setIsSubmittingFeedback(true);
    setError(null);
    setFeedbackSuccess(null);
    try {
      await apiService.submitFeedback({
        rating: feedbackForm.rating,
        body: feedbackForm.body.trim(),
        category: feedbackForm.category
      });
      setFeedbackSuccess('Thank you for your feedback!');
      setFeedbackForm({ rating: 0, body: '', category: null });
      // Reload feedback list to show new entry
      loadFeedback(true);
    } catch (err: any) {
      console.error('Failed to submit feedback:', err);
      setError(err.response?.data?.message || 'Failed to submit feedback');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const getCategoryIcon = (category: string | null) => {
    switch (category) {
      case 'bug': return <Bug size={14} />;
      case 'feature': return <Lightbulb size={14} />;
      case 'praise': return <ThumbsUp size={14} />;
      case 'complaint': return <ThumbsDown size={14} />;
      default: return <MessageCircle size={14} />;
    }
  };

  const getCategoryLabel = (category: string | null) => {
    switch (category) {
      case 'bug': return 'Bug Report';
      case 'feature': return 'Feature Request';
      case 'praise': return 'Praise';
      case 'complaint': return 'Complaint';
      case 'general': return 'General';
      default: return 'General';
    }
  };

  const handleEditProfile = () => {
    if (profile) {
      setProfileFormData({
        display_name: profile.display_name || '',
        company_name: profile.company_name || '',
        job_title: profile.job_title || '',
        phone: profile.phone || '',
        timezone: profile.timezone || ''
      });
      setIsEditingProfile(true);
    }
  };

  const setSection = (section: ActiveSection) => {
    historyModeRef.current = 'push';
    setActiveSection(section);
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    setProfileFormData({});
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      // Only send non-empty values
      const updates: UpdateProfileRequest = {};
      if (profileFormData.display_name !== undefined) {
        updates.display_name = profileFormData.display_name || null;
      }
      if (profileFormData.company_name !== undefined) {
        updates.company_name = profileFormData.company_name || null;
      }
      if (profileFormData.job_title !== undefined) {
        updates.job_title = profileFormData.job_title || null;
      }
      if (profileFormData.phone !== undefined) {
        updates.phone = profileFormData.phone || null;
      }
      if (profileFormData.timezone !== undefined) {
        updates.timezone = profileFormData.timezone || null;
      }

      const updatedProfile = await apiService.updateUserProfile(updates);
      setProfile(updatedProfile);
      setIsEditingProfile(false);
    } catch (err: any) {
      console.error('Failed to save profile:', err);
      setError(err.response?.data?.message || 'Failed to save profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleSettingChange = async (key: keyof UpdateSettingsRequest, value: any) => {
    if (!settings) return;

    setIsSavingSettings(true);
    try {
      const updates: UpdateSettingsRequest = { [key]: value };
      const updatedSettings = await apiService.updateUserSettings(updates);
      setSettings(updatedSettings);
    } catch (err: any) {
      console.error('Failed to update setting:', err);
      setError(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getThemeIcon = (mode: ThemeMode) => {
    switch (mode) {
      case 'light': return <Sun size={16} />;
      case 'dark': return <Moon size={16} />;
      case 'system': return <Monitor size={16} />;
    }
  };

  const completedSteps = new Set(profile?.onboarding?.steps_completed || []);
  const completedMilestoneCount = GETTING_STARTED_STEPS.filter((step) => completedSteps.has(step.key)).length;
  const gettingStartedComplete = completedMilestoneCount === GETTING_STARTED_STEPS.length;

  if (isLoading) {
    return (
      <div className="profile-tab">
        <div className="profile-container">
          <div className="loading-state">
            <RefreshCw size={24} className="spinning" />
            <span>Loading profile...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="profile-tab">
        <div className="profile-container">
          <div className="error-state">
            <AlertCircle size={24} />
            <span>{error}</span>
            <button className="retry-btn" onClick={loadData}>
              <RefreshCw size={16} />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-tab">
      <div className="profile-container">
        <div className="profile-section">
          {/* Section Header */}
          <div className="section-header">
            <div className="section-title">
              <User size={20} />
              <h2>Account</h2>
            </div>
            <div className="section-controls">
              <button
                className="refresh-btn"
                onClick={loadData}
                disabled={isLoading}
                title="Refresh"
              >
                <RefreshCw size={16} className={isLoading ? 'spinning' : ''} />
              </button>
            </div>
          </div>

          {/* Section Tabs */}
          <div className="section-tabs">
            <button
              className={`section-tab ${activeSection === 'profile' ? 'active' : ''}`}
              onClick={() => setSection('profile')}
            >
              <User size={16} />
              Profile
            </button>
            <button
              className={`section-tab ${activeSection === 'settings' ? 'active' : ''}`}
              onClick={() => setSection('settings')}
            >
              <Settings size={16} />
              Settings
            </button>
            <button
              className={`section-tab ${activeSection === 'feedback' ? 'active' : ''}`}
              onClick={() => setSection('feedback')}
            >
              <MessageSquare size={16} />
              Feedback
            </button>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="error-banner">
              <AlertCircle size={16} />
              <span>{error}</span>
              <button onClick={() => setError(null)}><X size={14} /></button>
            </div>
          )}

          {/* Profile Content */}
          {activeSection === 'profile' && profile && (
            <div className="tab-content">
              <div className="profile-content">
                {/* User Info Card */}
                <div className="user-info-card">
                  <div className="user-avatar">
                    {profile.avatar_url ? (
                      <img src={profile.avatar_url} alt="Avatar" />
                    ) : (
                      <User size={32} />
                    )}
                  </div>
                  <div className="user-details">
                    <h3>{profile.display_name || profile.email}</h3>
                    <div className="user-email">
                      <Mail size={14} />
                      <span>{profile.email}</span>
                      {profile.email_verified && (
                        <span className="verified-badge" title="Email verified">
                          <CheckCircle2 size={14} />
                        </span>
                      )}
                    </div>
                    {profile.auth && (
                      <div className="auth-info">
                        <Shield size={14} />
                        <span>Signed in via {profile.auth.provider || 'password'}</span>
                        {profile.auth.mfa_enabled && (
                          <span className="mfa-badge">MFA</span>
                        )}
                      </div>
                    )}
                  </div>
                  {!isEditingProfile && (
                    <button className="edit-btn" onClick={handleEditProfile}>
                      Edit Profile
                    </button>
                  )}
                </div>

                {/* Profile Fields */}
                <div className="profile-fields">
                  <h4>Profile Information</h4>

                  {isEditingProfile ? (
                    <div className="profile-form">
                      <div className="form-row">
                        <div className="form-group">
                          <label>
                            <User size={14} />
                            Display Name
                          </label>
                          <input
                            type="text"
                            value={profileFormData.display_name || ''}
                            onChange={(e) => setProfileFormData({
                              ...profileFormData,
                              display_name: e.target.value
                            })}
                            placeholder="Your display name"
                            maxLength={100}
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            <Building2 size={14} />
                            Company
                          </label>
                          <input
                            type="text"
                            value={profileFormData.company_name || ''}
                            onChange={(e) => setProfileFormData({
                              ...profileFormData,
                              company_name: e.target.value
                            })}
                            placeholder="Your company"
                            maxLength={200}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>
                            <Briefcase size={14} />
                            Job Title
                          </label>
                          <input
                            type="text"
                            value={profileFormData.job_title || ''}
                            onChange={(e) => setProfileFormData({
                              ...profileFormData,
                              job_title: e.target.value
                            })}
                            placeholder="Your job title"
                            maxLength={100}
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            <Phone size={14} />
                            Phone
                          </label>
                          <input
                            type="tel"
                            value={profileFormData.phone || ''}
                            onChange={(e) => setProfileFormData({
                              ...profileFormData,
                              phone: e.target.value
                            })}
                            placeholder="Your phone number"
                            maxLength={30}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>
                            <Globe size={14} />
                            Timezone
                          </label>
                          <select
                            value={profileFormData.timezone || ''}
                            onChange={(e) => setProfileFormData({
                              ...profileFormData,
                              timezone: e.target.value
                            })}
                          >
                            <option value="">Select timezone</option>
                            <option value="UTC">UTC</option>
                            <option value="America/New_York">Eastern Time (US)</option>
                            <option value="America/Chicago">Central Time (US)</option>
                            <option value="America/Denver">Mountain Time (US)</option>
                            <option value="America/Los_Angeles">Pacific Time (US)</option>
                            <option value="Europe/London">London</option>
                            <option value="Europe/Paris">Paris</option>
                            <option value="Europe/Berlin">Berlin</option>
                            <option value="Asia/Tokyo">Tokyo</option>
                            <option value="Asia/Shanghai">Shanghai</option>
                            <option value="Asia/Singapore">Singapore</option>
                            <option value="Australia/Sydney">Sydney</option>
                          </select>
                        </div>
                      </div>
                      <div className="form-actions">
                        <button
                          className="cancel-btn"
                          onClick={handleCancelEdit}
                          disabled={isSavingProfile}
                        >
                          <X size={16} />
                          Cancel
                        </button>
                        <button
                          className="save-btn"
                          onClick={handleSaveProfile}
                          disabled={isSavingProfile}
                        >
                          {isSavingProfile ? (
                            <RefreshCw size={16} className="spinning" />
                          ) : (
                            <Save size={16} />
                          )}
                          {isSavingProfile ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="profile-display">
                      <div className="display-row">
                        <div className="display-item">
                          <label><User size={14} /> Display Name</label>
                          <span>{profile.display_name || 'Not set'}</span>
                        </div>
                        <div className="display-item">
                          <label><Building2 size={14} /> Company</label>
                          <span>{profile.company_name || 'Not set'}</span>
                        </div>
                      </div>
                      <div className="display-row">
                        <div className="display-item">
                          <label><Briefcase size={14} /> Job Title</label>
                          <span>{profile.job_title || 'Not set'}</span>
                        </div>
                        <div className="display-item">
                          <label><Phone size={14} /> Phone</label>
                          <span>{profile.phone || 'Not set'}</span>
                        </div>
                      </div>
                      <div className="display-row">
                        <div className="display-item">
                          <label><Globe size={14} /> Timezone</label>
                          <span>{profile.timezone || 'Not set'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Account Info */}
                <div className="account-info">
                  <h4>Account Details</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label><Clock size={14} /> Member Since</label>
                      <span>{formatDate(profile.created_at)}</span>
                    </div>
                    <div className="info-item">
                      <label><Clock size={14} /> Last Login</label>
                      <span>{formatDate(profile.last_login_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Onboarding Progress */}
                {profile.onboarding && (
                  <div className="onboarding-section">
                    <div className="onboarding-header">
                      <h4>Getting Started</h4>
                      {gettingStartedComplete ? (
                        <span className="onboarding-status completed">
                          <CheckCircle2 size={14} />
                          Completed
                        </span>
                      ) : (
                        <span className="onboarding-status in-progress">
                          {completedMilestoneCount} of {GETTING_STARTED_STEPS.length} complete
                        </span>
                      )}
                    </div>
                    <p className="onboarding-description">Explore at your own pace. These optional steps help you get to know your workspace.</p>

                    {/* Progress Bar */}
                    <div
                      className="onboarding-progress-bar"
                      role="progressbar"
                      aria-label="Getting Started"
                      aria-valuemin={0}
                      aria-valuemax={GETTING_STARTED_STEPS.length}
                      aria-valuenow={completedMilestoneCount}
                    >
                      <div
                        className="progress-fill"
                        style={{
                          width: `${(completedMilestoneCount / GETTING_STARTED_STEPS.length) * 100}%`
                        }}
                      />
                    </div>

                    {/* Steps List */}
                    <div className="onboarding-steps">
                      {GETTING_STARTED_STEPS.map((step) => {
                        const isCompleted = completedSteps.has(step.key);
                        const StepIcon = step.icon;
                        return (
                          <div
                            key={step.key}
                            className={`onboarding-step ${isCompleted ? 'completed' : ''}`}
                          >
                            <div className="step-icon">
                              {isCompleted ? (
                                <CheckCircle2 size={18} />
                              ) : (
                                <Circle size={18} />
                              )}
                            </div>
                            <div className="step-content">
                              <div className="step-label">
                                <StepIcon size={14} />
                                {step.label}
                              </div>
                              <div className="step-description">{step.description}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Settings Content */}
          {activeSection === 'settings' && settings && (
            <div className="tab-content">
              <div className="settings-content">
                {/* Appearance */}
                <div className="settings-group">
                  <h4>Appearance</h4>
                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">
                        {getThemeIcon(settings.theme_mode as ThemeMode)}
                        Theme
                      </div>
                      <div className="setting-description">
                        Choose your preferred color theme
                      </div>
                    </div>
                    <div className="theme-selector">
                      {(['light', 'dark', 'system'] as ThemeMode[]).map((mode) => (
                        <button
                          key={mode}
                          className={`theme-option ${settings.theme_mode === mode ? 'active' : ''}`}
                          onClick={() => handleSettingChange('theme_mode', mode)}
                          disabled={isSavingSettings}
                        >
                          {getThemeIcon(mode)}
                          {mode.charAt(0).toUpperCase() + mode.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Notifications */}
                <div className="settings-group">
                  <h4>Notifications</h4>
                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">
                        <Bell size={16} />
                        Email Notifications
                      </div>
                      <div className="setting-description">
                        Receive important updates via email
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.notification_email}
                        onChange={(e) => handleSettingChange('notification_email', e.target.checked)}
                        disabled={isSavingSettings}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">
                        <AlertCircle size={16} />
                        Low Credit Alerts
                      </div>
                      <div className="setting-description">
                        Get notified when your credit balance is low
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.notification_low_credits}
                        onChange={(e) => handleSettingChange('notification_low_credits', e.target.checked)}
                        disabled={isSavingSettings}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">
                        <Mail size={16} />
                        Weekly Digest
                      </div>
                      <div className="setting-description">
                        Receive a weekly summary of your usage
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.notification_weekly_digest}
                        onChange={(e) => handleSettingChange('notification_weekly_digest', e.target.checked)}
                        disabled={isSavingSettings}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">
                        {settings.marketing_opt_in ? <Bell size={16} /> : <BellOff size={16} />}
                        Marketing Communications
                      </div>
                      <div className="setting-description">
                        Receive product updates and promotional content
                      </div>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={settings.marketing_opt_in}
                        onChange={(e) => handleSettingChange('marketing_opt_in', e.target.checked)}
                        disabled={isSavingSettings}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                <div className="settings-group">
                  <h4>Privacy</h4>
                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">
                        <Shield size={16} />
                        Cookie and storage choices
                      </div>
                      <div className="setting-description">
                        Review or withdraw optional analytics and affiliate referral measurement.
                      </div>
                    </div>
                    <button type="button" className="theme-option" onClick={openStorageSettings}>
                      Review settings
                    </button>
                  </div>
                </div>

                {/* API Preferences */}
                <div className="settings-group">
                  <h4>API Preferences</h4>
                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">
                        <Settings size={16} />
                        Default Model
                      </div>
                      <div className="setting-description">
                        Default LLM model for video processing
                      </div>
                    </div>
                    <div className="setting-control">
                      <select
                        className="setting-select"
                        value={settings.default_model}
                        onChange={(e) => handleSettingChange('default_model', e.target.value)}
                        disabled={isSavingSettings || isLoadingModelOptions || modelOptionsError !== null || processingModelOptions.length === 0}
                      >
                        {!processingModelOptions.some((option) => option.model_id === settings.default_model) && (
                          <option value={settings.default_model} disabled>
                            {settings.default_model}
                          </option>
                        )}
                        {processingModelOptions.map((option) => (
                          <option key={option.model_id} value={option.model_id}>
                            {option.label}{option.is_default ? ' (Default)' : ''}
                          </option>
                        ))}
                      </select>
                      {isLoadingModelOptions && (
                        <div className="setting-inline-status">
                          <RefreshCw size={14} className="spinning" />
                          <span>Loading model options</span>
                        </div>
                      )}
                      {modelOptionsError && (
                        <div className="setting-inline-status setting-inline-error">
                          <AlertCircle size={14} />
                          <span>{modelOptionsError}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="setting-item">
                    <div className="setting-info">
                      <div className="setting-label">
                        <Settings size={16} />
                        API Response Format
                      </div>
                      <div className="setting-description">
                        Level of detail in API responses
                      </div>
                    </div>
                    <select
                      className="setting-select"
                      value={settings.api_response_format}
                      onChange={(e) => handleSettingChange('api_response_format', e.target.value)}
                      disabled={isSavingSettings}
                    >
                      <option value="standard">Standard</option>
                      <option value="verbose">Verbose</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Feedback Content */}
          {activeSection === 'feedback' && (
            <div className="tab-content">
              <div className="feedback-content">
                {/* Submit Feedback Form */}
                <div className="feedback-form-card">
                  <h4>Share Your Feedback</h4>
                  <p className="feedback-form-description">
                    Help us improve by sharing your thoughts, reporting bugs, or suggesting new features.
                  </p>

                  {/* Success Message */}
                  {feedbackSuccess && (
                    <div className="feedback-success">
                      <CheckCircle2 size={16} />
                      <span>{feedbackSuccess}</span>
                      <button onClick={() => setFeedbackSuccess(null)}><X size={14} /></button>
                    </div>
                  )}

                  {/* Rating */}
                  <div className="feedback-field">
                    <label>Rating</label>
                    <div className="rating-selector">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          className={`rating-star ${feedbackForm.rating >= star ? 'active' : ''}`}
                          onClick={() => setFeedbackForm({ ...feedbackForm, rating: star })}
                          disabled={isSubmittingFeedback}
                          title={`${star} star${star > 1 ? 's' : ''}`}
                        >
                          <Star size={24} fill={feedbackForm.rating >= star ? 'currentColor' : 'none'} />
                        </button>
                      ))}
                      <span className="rating-label">
                        {feedbackForm.rating > 0 ? `${feedbackForm.rating} star${feedbackForm.rating > 1 ? 's' : ''}` : 'Select rating'}
                      </span>
                    </div>
                  </div>

                  {/* Category */}
                  <div className="feedback-field">
                    <label>Category (Optional)</label>
                    <div className="category-selector">
                      {[
                        { value: null, label: 'General', icon: <MessageCircle size={14} /> },
                        { value: 'bug', label: 'Bug', icon: <Bug size={14} /> },
                        { value: 'feature', label: 'Feature', icon: <Lightbulb size={14} /> },
                        { value: 'praise', label: 'Praise', icon: <ThumbsUp size={14} /> },
                        { value: 'complaint', label: 'Complaint', icon: <ThumbsDown size={14} /> }
                      ].map((cat) => (
                        <button
                          key={cat.value || 'general'}
                          className={`category-option ${feedbackForm.category === cat.value ? 'active' : ''}`}
                          onClick={() => setFeedbackForm({ ...feedbackForm, category: cat.value })}
                          disabled={isSubmittingFeedback}
                        >
                          {cat.icon}
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="feedback-field">
                    <label>Your Feedback</label>
                    <textarea
                      value={feedbackForm.body}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, body: e.target.value })}
                      placeholder="Tell us what you think..."
                      maxLength={5000}
                      rows={4}
                      disabled={isSubmittingFeedback}
                    />
                    <span className="char-count">{feedbackForm.body.length}/5000</span>
                  </div>

                  {/* Submit Button */}
                  <button
                    className="submit-feedback-btn"
                    onClick={handleSubmitFeedback}
                    disabled={isSubmittingFeedback || feedbackForm.rating === 0 || !feedbackForm.body.trim()}
                  >
                    {isSubmittingFeedback ? (
                      <>
                        <RefreshCw size={16} className="spinning" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Submit Feedback
                      </>
                    )}
                  </button>
                </div>

                {/* Previous Feedback */}
                <div className="feedback-history">
                  <h4>Your Previous Feedback</h4>

                  {isLoadingFeedback && feedbackList.length === 0 ? (
                    <div className="feedback-loading">
                      <RefreshCw size={20} className="spinning" />
                      <span>Loading feedback...</span>
                    </div>
                  ) : feedbackList.length === 0 ? (
                    <div className="feedback-empty">
                      <MessageSquare size={32} />
                      <p>You haven't submitted any feedback yet.</p>
                    </div>
                  ) : (
                    <>
                      <div className="feedback-list">
                        {feedbackList.map((feedback) => (
                          <div key={feedback.feedback_id} className="feedback-item">
                            <div className="feedback-item-header">
                              <div className="feedback-rating">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    size={14}
                                    className={feedback.rating >= star ? 'filled' : ''}
                                    fill={feedback.rating >= star ? 'currentColor' : 'none'}
                                  />
                                ))}
                              </div>
                              {feedback.category && (
                                <span className={`feedback-category ${feedback.category}`}>
                                  {getCategoryIcon(feedback.category)}
                                  {getCategoryLabel(feedback.category)}
                                </span>
                              )}
                              <span className="feedback-date">
                                {formatDate(feedback.created_at)}
                              </span>
                            </div>
                            <p className="feedback-body">{feedback.body}</p>
                          </div>
                        ))}
                      </div>
                      {feedbackHasMore && (
                        <button
                          className="load-more-btn"
                          onClick={() => loadFeedback(false)}
                          disabled={isLoadingFeedback}
                        >
                          {isLoadingFeedback ? (
                            <>
                              <RefreshCw size={14} className="spinning" />
                              Loading...
                            </>
                          ) : (
                            'Load More'
                          )}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;
